import {
    Alert,
    Box,
    CircularProgress,
    Container,
    Typography
} from "@mui/material";
import React, { useEffect, useMemo, useState } from "react";
import ReactMarkdown from "react-markdown";
import { useAppContext } from "../context/AppContext";

const DEFAULT_WAIVER = "waiver-athlete.md";

const LiabilityWaiver: React.FC = () => {
    const { organization } = useAppContext();
    const [markdownContent, setMarkdownContent] = useState<string>("");
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);

    const waiverName = useMemo(() => {
        const name = organization?.waiver_name || DEFAULT_WAIVER;
        return /^[a-zA-Z0-9_.-]+$/.test(name) ? name : DEFAULT_WAIVER;
    }, [organization?.waiver_name]);

    useEffect(() => {
        const loadMarkdown = async () => {
            try {
                const response = await fetch(`/waivers/${waiverName}`);
                if (response.ok) {
                    const text = await response.text();
                    // Replace form placeholders with descriptive text for public viewing
                    const processedText = text
                        .replace(/\[FORM:digital_consent\]/g,
                            '\n\n**Digital Communication Methods:** Email, Text Message, Zoom/Video Call\n\n')
                        .replace(/\[FORM:media_release\]/g,
                            '\n\n**Media Release Options:**\n- I give permission for photos, videos, or recordings from sessions to be used for training, marketing, or educational purposes.\n- I do NOT give permission for any media to be used publicly.\n\n')
                        .replace(/\[FORM:emergency_contact\]/g,
                            '\n\n**Emergency Contact Information:** [To be provided during registration]\n\n')
                        .replace(/\[FORM:signature\]/g,
                            '\n\n**Signature Section:** [Electronic signature required during registration]\n\n');
                    setMarkdownContent(processedText);
                } else {
                    setError('Failed to load liability waiver document.');
                }
            } catch (err) {
                console.error('Error loading liability waiver:', err);
                setError('Failed to load liability waiver document. Please try again later.');
            } finally {
                setLoading(false);
            }
        };

        loadMarkdown();
    }, [waiverName]);

    if (loading) {
        return (
            <Box>
                <Container maxWidth="lg" sx={{ py: 8 }}>
                    <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                        <CircularProgress size={60} />
                    </Box>
                </Container>
            </Box>
        );
    }

    if (error) {
        return (
            <Box>
                <Container maxWidth="lg" sx={{ py: 8 }}>
                    <Alert severity="error" sx={{ mb: 4 }}>
                        {error}
                    </Alert>
                </Container>
            </Box>
        );
    }

    return (
        <Box>
            <Container maxWidth="lg" sx={{ py: 8 }}>
                <Typography variant="h2" component="h1" sx={{
                    textAlign: 'center',
                    mb: 4,
                    fontWeight: 'bold'
                }}>
                    Liability Waiver Agreement
                </Typography>

                <Box
                    sx={{
                        maxWidth: 900,
                        mx: 'auto',
                        '& p': { margin: 0, lineHeight: 1.8 },
                        '& p + p': { marginTop: 2 },
                        '& h1, & h2, & h3, & h4, & h5, & h6': {
                            marginTop: 4,
                            marginBottom: 2,
                            fontWeight: 'bold',
                            color: 'text.primary'
                        },
                        '& h1': {
                            fontSize: '2rem'
                        },
                        '& h2': {
                            fontSize: '1.5rem'
                        },
                        '& ul, & ol': {
                            marginTop: 2,
                            marginBottom: 2,
                            paddingLeft: 3
                        },
                        '& li': {
                            marginBottom: 1,
                            lineHeight: 1.8
                        },
                        '& hr': {
                            marginTop: 4,
                            marginBottom: 4,
                            borderColor: 'divider'
                        },
                        '& strong': {
                            fontWeight: 'bold'
                        },
                        '& blockquote': {
                            borderLeft: '4px solid',
                            borderColor: 'primary.main',
                            paddingLeft: 2,
                            marginLeft: 0,
                            fontStyle: 'italic',
                            color: 'text.secondary'
                        }
                    }}
                >
                    <ReactMarkdown>{markdownContent}</ReactMarkdown>
                </Box>

                <Box sx={{ mt: 6, textAlign: 'center' }}>
                    <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        This is a public version of the Liability Waiver Agreement. To sign this waiver, please register for an account.
                    </Typography>
                </Box>
            </Container>
        </Box>
    );
};

export default LiabilityWaiver;
